/**
 * Вспомогательные связи потолочного света с настенными электроприборами.
 *
 * Это не кабельная трасса: пользователь создаёт связь отдельным инструментом,
 * выбирая её форму. Линию можно выделить и удалить как любой объект чертежа.
 */
import type { PointerEvent } from "react";
import type { ItemsLayer, LightingConnection, WallGraph } from "../../plan";
import { cableEndPoint } from "../../plan";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";

interface Props {
	graph: WallGraph;
	electrical: ItemsLayer;
	connections: LightingConnection[];
	mmPerPx: number;
	onConnectionPointerDown: (
		event: PointerEvent<SVGPathElement>,
		connection: LightingConnection,
	) => void;
}

export const LightingConnections2D = ({
	graph,
	electrical,
	connections,
	mmPerPx,
	onConnectionPointerDown,
}: Props) => {
	const selection = useEditorStore((s) => s.selection);
	const tool = useEditorStore((s) => s.tool);
	const pathFor = (fromId: string, toId: string, style: LightingConnection["style"]) => {
		const from = cableEndPoint(graph, electrical, fromId);
		const to = cableEndPoint(graph, electrical, toId);
		if (!from || !to) return null;
		if (style === "line") return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
		const dx = to.x - from.x;
		const dy = to.y - from.y;
		const distance = Math.hypot(dx, dy);
		if (distance === 0) return null;
		const bend = Math.min(Math.max(distance * 0.2, 200), 800);
		return `M ${from.x} ${from.y} Q ${(from.x + to.x) / 2 - (dy / distance) * bend} ${(from.y + to.y) / 2 + (dx / distance) * bend} ${to.x} ${to.y}`;
	};

	return (
		<g fill="none" stroke="#2563eb" strokeWidth={mmPerPx} opacity={0.8}>
			{connections.map((connection) => {
				const path = pathFor(connection.from, connection.to, connection.style);
				if (!path) return null;
				const selected =
					selection?.kind === SelectionKind.LightingConnection && selection.id === connection.id;
				const interactive = tool === Tool.Select || tool === Tool.Erase;
				return (
					<g
						key={connection.id}
						style={{
							cursor: interactive ? "pointer" : "default",
							pointerEvents: interactive ? "auto" : "none",
						}}
					>
						{selected && <path d={path} stroke="#2f6feb33" strokeWidth={7 * mmPerPx} />}
						<path d={path} strokeDasharray={`${2 * mmPerPx} ${3 * mmPerPx}`} />
						<path
							data-role="lightingConnection"
							data-id={connection.id}
							d={path}
							stroke="rgba(0, 0, 0, 0.001)"
							strokeWidth={10 * mmPerPx}
							pointerEvents="stroke"
							onPointerDown={(event) => onConnectionPointerDown(event, connection)}
						/>
					</g>
				);
			})}
		</g>
	);
};
